import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Chip, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";

const currency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(145deg,#EEF5FF 0%,#F8FAFC 100%)",
    padding: theme.spacing(2),
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1)
    }
  },
  shell: {
    maxWidth: 1180,
    margin: "0 auto"
  },
  hero: {
    borderRadius: 32,
    padding: theme.spacing(3),
    background: "linear-gradient(135deg,#071B4D,#0066FF,#00A3FF)",
    color: "#fff",
    boxShadow: "0 24px 70px rgba(0,102,255,.24)",
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: 950,
    fontSize: "2rem",
    lineHeight: 1.05,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.45rem"
    }
  },
  subtitle: {
    opacity: .88,
    marginTop: 10,
    fontSize: ".95rem",
    maxWidth: 720,
    lineHeight: 1.5
  },
  searchCard: {
    borderRadius: 26,
    background: "#fff",
    border: "1px solid #E2E8F0",
    padding: theme.spacing(1.3),
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    boxShadow: "0 16px 40px rgba(15,23,42,.07)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 18,
      background: "#F8FAFC"
    }
  },
  tabs: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },
  tab: {
    borderRadius: 999,
    border: "1px solid #E2E8F0",
    background: "#fff",
    padding: "10px 15px",
    fontWeight: 900,
    cursor: "pointer",
    color: "#475569"
  },
  tabActive: {
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    borderColor: "transparent"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: theme.spacing(1.4)
  },
  card: {
    borderRadius: 30,
    overflow: "hidden",
    background: "#fff",
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 45px rgba(15,23,42,.08)"
  },
  image: {
    height: 190,
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 46,
    color: "#0066FF",
    position: "relative",
    overflow: "hidden"
  },
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  badge: {
    position: "absolute",
    top: 12,
    left: 12,
    background: "rgba(255,255,255,.94)",
    border: "1px solid rgba(0,102,255,.16)",
    borderRadius: 999,
    padding: "6px 10px",
    fontWeight: 950,
    fontSize: ".68rem",
    color: "#0066FF"
  },
  body: {
    padding: theme.spacing(1.5)
  },
  name: {
    fontWeight: 950,
    color: "#0F172A",
    fontSize: "1.05rem",
    lineHeight: 1.2
  },
  desc: {
    color: "#64748B",
    fontSize: ".78rem",
    marginTop: 8,
    lineHeight: 1.45,
    minHeight: 44,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  price: {
    fontWeight: 950,
    color: "#0066FF",
    fontSize: "1.25rem",
    marginTop: 12
  },
  cta: {
    marginTop: 14,
    borderRadius: 18,
    fontWeight: 950,
    textTransform: "none",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    padding: theme.spacing(1.1)
  },
  empty: {
    borderRadius: 26,
    background: "#fff",
    border: "1px dashed #CBD5E1",
    padding: 30,
    textAlign: "center",
    color: "#64748B",
    fontWeight: 900
  }
}));

const PublicCatalog = () => {
  const classes = useStyles();
  const { companyId } = useParams();
  const [items, setItems] = useState([]);
  const [company, setCompany] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    async function load() {
      const { data } = await api.get(
        `/public/catalog/${companyId}`,
        {
          params: { searchParam, type }
        }
      );

      if (Array.isArray(data)) {
        setItems(data);
        setCompany(null);
        return;
      }

      setItems(
        Array.isArray(data?.items)
          ? data.items
          : []
      );

      setCompany(data?.company || null);
    }
    load();
  }, [companyId, searchParam, type]);

  const categories = useMemo(
    () => Array.from(new Set(items.map(i => i.category).filter(Boolean))).slice(0, 8),
    [items]
  );

  const openWhatsappInterest = item => {
    const rawPhone =
      company?.whatsapp ||
      company?.phone ||
      "";

    let phone = String(rawPhone)
      .replace(/\D/g, "");

    if (
      phone.length >= 10 &&
      phone.length <= 11 &&
      !phone.startsWith("55")
    ) {
      phone = `55${phone}`;
    }

    if (!phone) {
      window.alert(
        "O WhatsApp desta empresa ainda não está disponível."
      );
      return;
    }

    const selectedPrice =
      item?.promoPrice ||
      item?.price ||
      0;

    const catalogLink =
      `${window.location.origin}${window.location.pathname}` +
      `#produto-${item.id}`;

    const message = [
      "Olá! Tenho interesse neste produto:",
      "",
      `*${item.name}*`,
      `Valor: ${currency(selectedPrice)}`,
      `Código: #${item.id}`,
      "",
      `Link: ${catalogLink}`
    ].join("\n");

    const whatsappUrl =
      `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(
      whatsappUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.shell}>
        <section className={classes.hero}>
          <Typography className={classes.title}>
            {company?.name
              ? `Catálogo ${company.name}`
              : "Catálogo inteligente"}
          </Typography>
          <Typography className={classes.subtitle}>
            Conheça produtos, serviços e ofertas disponíveis. Escolha um item e fale pelo WhatsApp para comprar, agendar ou pedir orçamento.
          </Typography>
        </section>

        <section className={classes.searchCard}>
          <TextField
            className={classes.field}
            variant="outlined"
            placeholder="Buscar produto ou serviço..."
            value={searchParam}
            onChange={e => setSearchParam(e.target.value)}
          />

          <div className={classes.tabs}>
            {[
              ["", "Tudo"],
              ["product", "Produtos"],
              ["service", "Serviços"],
              ["combo", "Combos"]
            ].map(([value, label]) => (
              <button
                key={label}
                className={`${classes.tab} ${type === value ? classes.tabActive : ""}`}
                onClick={() => setType(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {!!categories.length && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {categories.map(c => <Chip key={c} label={c} style={{ fontWeight: 900 }} />)}
          </div>
        )}

        <main className={classes.grid}>
          {items.map(item => (
            <article
              key={item.id}
              id={`produto-${item.id}`}
              className={classes.card}
            >
              <div className={classes.image}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className={classes.img} />
                ) : (
                  item.type === "service" ? "🧾" : item.type === "combo" ? "🎁" : "📦"
                )}

                <span className={classes.badge}>
                  {item.type === "service" ? "Serviço" : item.type === "combo" ? "Combo" : "Produto"}
                </span>
              </div>

              <div className={classes.body}>
                <Typography className={classes.name}>{item.name}</Typography>
                <Typography className={classes.desc}>
                  {item.subtitle || item.description || "Entre em contato para saber mais."}
                </Typography>

                <div className={classes.price}>
                  {currency(item.promoPrice || item.price)}
                </div>

                <Button
                  fullWidth
                  className={classes.cta}
                  onClick={() =>
                    openWhatsappInterest(item)
                  }
                >
                  {item.ctaText || "Tenho interesse"}
                </Button>
              </div>
            </article>
          ))}
        </main>

        {!items.length && (
          <div className={classes.empty}>
            Nenhum item disponível neste catálogo.
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicCatalog;
